import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Scissors } from "@phosphor-icons/react";
import { API, saveToken } from "@/lib/api";
import { useLocale } from "@/i18n";
import FlagToggle from "@/components/FlagToggle";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { t, locale, setLocale } = useLocale();
  const { lang } = useParams();

  useEffect(() => {
    if (lang === "py") setLocale("es");
    else if (lang === "br") setLocale("pt");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const [shopName, setShopName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (lang && !["br", "py"].includes(lang)) return <Navigate to="/cadastro" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (shopName.trim().length < 2) return toast.warning(t("warn_shop"));
    if (whatsapp.replace(/\D/g, "").length < 8) return toast.warning(t("warn_wa"));
    if (password.length < 6) return toast.warning(t("warn_pwd"));
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        shop_name: shopName.trim(),
        whatsapp,
        password,
        country: locale === "es" ? "py" : "br",
      });
      saveToken(res.data.token);
      toast.success(t("reg_success"));
      navigate("/painel");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("reg_error"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "mt-1.5 w-full h-12 px-4 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors";

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <div className="w-full max-w-md mx-auto px-4 min-h-screen flex flex-col justify-center py-10" data-testid="register-page">
        <div className="flex items-center justify-between mb-2">
          <Link to="/" className="flex items-center gap-2 text-amber-500">
            <Scissors size={20} weight="duotone" />
            <span className="font-display font-bold">Barber Menu</span>
          </Link>
          <FlagToggle />
        </div>
        <h1 className="text-3xl font-black tracking-tight">{t("reg_title")}</h1>
        <p className="text-sm text-zinc-500 mt-2">{t("reg_sub")}</p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="register-form">
          <div>
            <label className="text-sm font-semibold text-zinc-300">{t("shop_label")}</label>
            <input
              data-testid="register-shop-input"
              type="text"
              value={shopName}
              onChange={(e) => setShopName(e.target.value)}
              placeholder={t("shop_ph")}
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-zinc-300">{t("wa_label")}</label>
            <input
              data-testid="register-whatsapp-input"
              type="tel"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder={t("wa_ph")}
              autoComplete="tel"
              className={inputCls}
            />
            <p className="text-xs text-zinc-600 mt-1.5">{t("wa_note")}</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-zinc-300">{t("pwd_label")}</label>
            <input
              data-testid="register-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("pwd_ph")}
              autoComplete="new-password"
              className={inputCls}
            />
          </div>
          <button
            data-testid="register-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-lg bg-amber-500 text-zinc-950 font-bold hover:bg-amber-400 transition-colors active:scale-95 disabled:opacity-60"
          >
            {loading ? t("creating") : t("reg_btn")}
          </button>
        </form>

        <p className="text-sm text-zinc-500 mt-6 text-center">
          {t("have_account")}{" "}
          <Link to="/entrar" data-testid="go-login-link" className="text-amber-500 font-semibold hover:underline">
            {t("enter")}
          </Link>
        </p>
      </div>
    </div>
  );
}
